/** @jsx jsx */
import { jsx } from '@emotion/core'
import PropTypes from 'prop-types'
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useCallback,
  useContext,
} from 'react'
import { connect } from 'react-redux'
import styled from '@emotion/styled'
import {
  Content,
  Controls,
  Filler,
  Title,
  FullHeightSizer,
  NoDataIndicator,
} from '../screen-util'
import { Button, Input, Popconfirm, message } from 'antd'
import { SpinContext, ErrorContext } from 'context'
import { List } from 'react-virtualized'

import { getApiTargetInfo, ExportComponent } from '../../core'

const GenericEditScreen = ({
  title, // title of screen
  loadApiTask, // targetInfo => (/*some params*/) =>  data.Task usually comes from the api file
  getLoadCriteria, // {} , returns criteria object which will be feeded to load api
  criteria, // function which returns extra selecting which are used in retrieve task
  createApiTask, // targetInfo => (/*some params*/) =>  data.Task usually comes from the api file
  updateApiTask, // targetInfo => (/*some params*/) =>  data.Task usually comes from the api file
  deleteApiTask, // targetInfo => (/*some params*/) =>  data.Task usually comes from the api file
  successUpdateMessage, // message that shows up on top of the screen after successful updates
  successCreateMessage, // message that shows up on top of the screen after successful creates
  successDeleteMessage, // message that shows up on top of the screen after successful deletes
  renderModal, // renders the popup on edit click
  renderRow, // render for the each item row
  sort, // (itemA,itemB) => Number
  acceptFilter, // function item => string => boolean
  dataMatcher, // itemInList => updateResponse => boolean // used for updating the listen based on update response
  newButtonText, // create button text
  listHeightOffset, // height offset for the virtualized list. This is subtracted from the screen height
  rowHeight, // row height
  apiTargetInfo, // Url - state to props
  isExportAvaliable = false, // showing export button
  exportHeaders = [],
  exportFunc = data => data,
  ...props
}) => {
  const [list, setList] = useState([])
  const [fuzzyFilter, setFuzzyFilter] = useState(null)

  const { spinning, setSpinning } = useContext(SpinContext)
  const isLoading = spinning

  //= useState(true)

  const retrieve = useMemo(_ => loadApiTask(apiTargetInfo), [
    apiTargetInfo,
    loadApiTask,
  ])

  const createForm = createApiTask(apiTargetInfo)

  const deleteForm = deleteApiTask(apiTargetInfo)

  const updateForm = updateApiTask(apiTargetInfo)

  const [modalInfo, setModalInfo] = useState(null)
  const hideModal = () => setModalInfo(null)
  const popNewModal = () => setModalInfo({ isNew: true })
  const { setError } = useContext(ErrorContext)
  const popModal = setModalInfo

  const loadData = useCallback(() => {
    setSpinning(true)
    retrieve(getLoadCriteria())
      .map(resp => {
        setSpinning(false)
        return resp
      })
      .fork(setError, setList)
  }, [getLoadCriteria, retrieve, setError, setSpinning])

  useEffect(() => {
    retrieve(getLoadCriteria()).fork(setError, data => {
      setList(data)
      setSpinning(false)
    })
    // initial load only
  }, [getLoadCriteria, retrieve, setError, setSpinning])

  const deleteTask = useCallback(
    data => {
      setSpinning(true)
      return deleteForm(data).map(resp => {
        setSpinning(false)
        setList(ts => ts.filter(t => !dataMatcher(data)(t)))
        props.afterSaveCall && props.afterSaveCall(resp)
        return resp
      })
    },
    [dataMatcher, deleteForm, props, setSpinning]
  )
  const updateTask = useCallback(
    data => {
      setSpinning(true)
      return updateForm(data).bimap(
        err => {
          setSpinning(false)
          return err
        },
        resp => {
          message.success(successUpdateMessage)
          setSpinning(false)
          setList(ts => ts.map(t => (dataMatcher(t)(data) ? data : t)))
          props.afterSaveCall && props.afterSaveCall(resp)
          return resp
        }
      )
    },
    [dataMatcher, props, setSpinning, successUpdateMessage, updateForm]
  )

  const createTask = data => {
    return createForm(data)
      .map(resp => {
        //      message.info('Data Saved')
        message.success(successCreateMessage)
        setList([{ ...data, isNew: false }, ...list])
        return resp
      })
      .map(resp => {
        props.afterSaveCall && props.afterSaveCall(resp)
        return resp
      })

    //    props.afterSaveCall && props.afterSaveCall()
  }

  // const createTask = newData => {
  //   const data = { ...newData, isNew: undefined }
  //   setSpinning(true)
  //   return createForm(data).bimap(
  //     err => {
  //       setSpinning(false)
  //       return err
  //     },
  //     resp => {
  //       setSpinning(false)
  //       message.success(successCreateMessage)
  //       setList([...list, data])
  //       return resp
  //     }
  //   )
  // }

  const onDelete = useCallback(
    data => {
      deleteTask(data).fork(setError, () => {
        message.success(`${successDeleteMessage}`)
        loadData()
      })
    },
    [deleteTask, loadData, setError, successDeleteMessage]
  )

  const displayedList = useMemo(
    () =>
      (sort != null ? sort(list) : list).filter(
        i => acceptFilter == null || acceptFilter(i)(fuzzyFilter)
      ),
    [sort, list, acceptFilter, fuzzyFilter]
  )
  const ref = useRef()

  useLayoutEffect(() => {
    const handleResize = () => {
      if (ref.current != null) ref.current.recomputeRowHeights()
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const virtualizedRender = useCallback(
    props =>
      renderRow({
        ...props,
        list: displayedList,
        item: displayedList[props.index],
        popModal,
        onDelete,
        updateTask,
        isLoading,
        loadData,
      }),
    // updateTask will never change once initialized

    [
      renderRow,
      displayedList,
      popModal,
      onDelete,
      updateTask,
      isLoading,
      loadData,
    ]
  )
  return (
    <Content style={{ marginTop: '0' }}>
      {modalInfo &&
        renderModal({
          hideModal,
          modalInfo,
          isLoading,
          updateTask,
          createTask,
          list: displayedList,
        })}
      <Controls>
        <Title>{title}</Title>
        {criteria && criteria({ loadData })}
        <Filler />
        <Input.Search
          placeholder="Filter"
          value={fuzzyFilter}
          onChange={e => setFuzzyFilter(e.target.value)}
          style={{
            marginLeft: '10px',
            marginRight: '10px',
            flex: '0 0 200px',
          }}
        />
        <Button
          loading={isLoading}
          type="primary"
          onClick={loadData}
          style={{ marginRight: '10px' }}
        >
          Refresh
        </Button>
        {isExportAvaliable && (
          <ExportComponent
            getData={_ => exportFunc(displayedList)}
            data={displayedList}
            headers={exportHeaders.map(d => ({
              ...d,
              rowHeaderBackgroundColor: '#eee',
            }))}
            fileName={title}
            btnStyle={{ marginRight: '10px' }}
            disabled={displayedList.length <= 0}
          />
        )}
      </Controls>
      <Button
        type="dashed"
        onClick={popNewModal}
        style={{ marginBottom: '24px' }}
        block
      >
        {newButtonText}
      </Button>
      <FullHeightSizer heightOffset={listHeightOffset}>
        {props => (
          <List
            ref={ref}
            {...props}
            style={{ outline: 'none' }}
            rowRenderer={virtualizedRender}
            rowCount={displayedList.length}
            noRowsRenderer={() => (
              <NoDataIndicator>
                {list.length === 0
                  ? 'No data retrieved'
                  : 'No data matched filter'}
              </NoDataIndicator>
            )}
            rowHeight={rowHeight}
          />
        )}
      </FullHeightSizer>
    </Content>
  )
}
GenericEditScreen.propTypes = {
  title: PropTypes.node.isRequired,
  loadApiTask: PropTypes.func.isRequired,
  getLoadCriteria: PropTypes.func,
  criteria: PropTypes.func,
  createApiTask: PropTypes.func.isRequired,
  updateApiTask: PropTypes.func.isRequired,
  deleteApiTask: PropTypes.func.isRequired,
  successUpdateMessage: PropTypes.string,
  successCreateMessage: PropTypes.string,
  successDeleteMessage: PropTypes.string,
  renderModal: PropTypes.func.isRequired,
  dataMatcher: PropTypes.func.isRequired,
  renderRow: PropTypes.func.isRequired,
  sort: PropTypes.func,
  acceptFilter: PropTypes.func.isRequired,
  newButtonText: PropTypes.node,
  listHeightOffset: PropTypes.number,
  rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
}
GenericEditScreen.defaultProps = {
  successUpdateMessage: 'Data updated',
  successCreateMessage: 'Data created',
  successDeleteMessage: 'Data deleted',
  getLoadCriteria: () => ({}),
  criteria: null,
  dataMatcher: d1 => d2 => (d1.id || d1.ident) === (d2.id || d2.ident),
  newButtonText: 'New [+]',
  listHeightOffset: 330,
  rowHeight: 35,
}
// export default GenericEditScreen
export const RowButtons = props => {
  const { item, onDelete, popModal, hideDuplicate } = props
  const deleteClicked = props.deleteClicked || (() => onDelete(item))
  const editClicked = props.editClicked || (() => popModal(item))
  const extraButtonEvent = props.extraButtonEvent
  const extraButtonText = props.extraButtonText
  const duplicateClicked = props.duplicateClicked
  return (
    <Button.Group style={{ display: 'flex' }}>
      <Button onClick={editClicked}>Edit</Button>
      {extraButtonEvent && (
        <Button onClick={extraButtonEvent}>
          {extraButtonText || 'Duplicate'}
        </Button>
      )}
      {!hideDuplicate && duplicateClicked && (
        <Button onClick={duplicateClicked}>Duplicate</Button>
      )}
      <Popconfirm
        title="Are you sure?"
        okText="Yes"
        cancelText="No"
        onConfirm={deleteClicked}
        placement="topRight"
      >
        <Button>Remove</Button>
      </Popconfirm>
    </Button.Group>
  )
}
RowButtons.propTypes = {
  item: PropTypes.object.isRequired,
  onDelete: PropTypes.func.isRequired,
  popModal: PropTypes.func.isRequired,
  deleteClicked: PropTypes.func,
  editClicked: PropTypes.func,
  extraButtonEvent: PropTypes.func,
  extraButtonText: PropTypes.string,
}
export const RowContainer = styled.div({
  display: 'flex',
  borderBottom: '1px solid #eee',
  alignItems: 'center',
  '&:hover': {
    backgroundColor: '#f2f2f2',
  },
  paddingLeft: '.5em',
  paddingRight: '.5em',
})
export const RowTitle = styled.a({
  color: '#333',
  textDecoration: 'none',
  fontSize: '1.2em',
  marginRight: '10px',
})
const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
})
export default connect(mapStateToProps)(GenericEditScreen)
